<template>
  <nav
    class="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] z-50 nav-outer"
  >
    <!-- Контентная часть — всегда 72px -->
    <div class="flex items-end nav-content">
      <!-- Профиль -->
      <RouterLink
        to="/profile"
        class="nav-tab relative"
        :class="{
          'nav-active': isActive('/profile'),
          'nav-first': true,
        }"
        :style="isActive('/profile') ? 'background: #FAF3E8; z-index: 2; border-radius: 0 0 14px 0;' : 'background: transparent; z-index: 1; border-radius: 0 0 14px 14px;'"
      >
        <img
          v-if="avatarSrc && !avatarError"
          :src="avatarSrc"
          class="w-7 h-7 rounded-full object-cover"
          alt=""
          @error="avatarError = true"
        />
        <svg v-else width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="#41291A" stroke-width="1.5" stroke-linecap="round">
          <circle cx="14" cy="14" r="12"/>
          <circle cx="14" cy="11" r="4"/>
          <path d="M6 23c0-4 3.6-6 8-6s8 2 8 6"/>
        </svg>
      </RouterLink>

      <!-- Зоя — логотип "3" Зожника -->
      <RouterLink
        to="/zoya"
        data-guide="nav-zoya"
        @click="guide.done('ask_zoya'); guide.journey('j_zoya')"
        class="nav-tab relative"
        :class="{ 'nav-active': isActive('/zoya') }"
        :style="isActive('/zoya') ? 'background: #FAF3E8; z-index: 2; border-radius: 0 0 14px 14px;' : 'background: transparent; z-index: 1; border-radius: 0 0 14px 14px;'"
      >
        <img
          v-if="!logoError"
          src="/Zoya_logo_transparent.png"
          alt="Zoya"
          class="h-8 w-auto object-contain"
          :style="logoLoaded ? '' : 'position:absolute;opacity:0;pointer-events:none'"
          @load="logoLoaded = true"
          @error="logoError = true"
        />
        <span v-if="!logoLoaded" style="font-family: Inter, sans-serif; font-size: var(--fs-18); font-weight: 400; color: var(--ink);">Зоя</span>
        <!-- Значок: фаза 2 онбординга или непрочитанные уведомления от Зои -->
        <Transition name="badge-fade">
          <span v-if="badgeVisible" class="zoya-badge">{{ badgeText }}</span>
        </Transition>
      </RouterLink>

      <!-- База продуктов / поиск -->
      <RouterLink
        to="/knowledge"
        class="nav-tab relative"
        :class="{ 'nav-active': isActive('/knowledge') }"
        :style="isActive('/knowledge') ? 'background: #FAF3E8; z-index: 2; border-radius: 0 0 14px 14px;' : 'background: transparent; z-index: 1; border-radius: 0 0 14px 14px;'"
      >
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" :stroke="isActive('/knowledge') ? '#41291A' : '#41291A'" stroke-width="1.5" stroke-linecap="round">
          <circle cx="13" cy="13" r="9"/>
          <line x1="20" y1="20" x2="25" y2="25"/>
        </svg>
      </RouterLink>

      <!-- Кулинария — тарелка с крышкой -->
      <RouterLink
        to="/cooking"
        class="nav-tab relative"
        :class="{ 'nav-active': isActive('/cooking') }"
        :style="isActive('/cooking') ? 'background: #FAF3E8; z-index: 2; border-radius: 0 0 14px 14px;' : 'background: transparent; z-index: 1; border-radius: 0 0 14px 14px;'"
      >
        <svg width="24" height="21" viewBox="0 0 14 12" fill="none" stroke="#41291A" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
          <path d="M7 2C8.72391 2 10.3772 2.68482 11.5962 3.90381C12.8152 5.12279 13.5 6.77609 13.5 8.5C13.5 8.76522 13.3946 9.0196 13.2071 9.2071C13.0196 9.3946 12.7652 9.5 12.5 9.5H1.5C1.23478 9.5 0.98043 9.3946 0.792893 9.2071C0.605357 9.0196 0.5 8.76522 0.5 8.5C0.5 6.77609 1.18482 5.12279 2.40381 3.90381C3.62279 2.68482 5.27609 2 7 2Z"/>
          <path d="M7 2V0.5"/>
          <path d="M0.5 11.5H13.5"/>
        </svg>
      </RouterLink>

      <!-- Дневник — круг с улыбкой -->
      <RouterLink
        to="/diary"
        class="nav-tab relative"
        :class="{
          'nav-active': isActive('/diary'),
          'nav-last': true,
        }"
        :style="isActive('/diary') ? 'background: #FAF3E8; z-index: 2; border-radius: 0 0 0 14px;' : 'background: transparent; z-index: 1; border-radius: 0 0 14px 14px;'"
      >
        <svg width="28" height="28" viewBox="0 0 38 38" fill="none" :stroke="isActive('/diary') ? '#41291A' : '#41291A'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="18.9758" cy="18.9758" r="17.9758"/>
          <path d="M18.6758 13.7847L18.6758 21.7847"/>
          <path d="M14.9766 17.8394L22.9766 17.8394"/>
          <path d="M9.16992 25.5879C13.4366 32.5814 23.7496 32.937 29.2022 25.5879"/>
        </svg>
      </RouterLink>
    </div>

  </nav>
</template>

<script setup>
import { useRoute } from 'vue-router'
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '../store/auth.js'
import { imgUrl } from '../utils/image.js'
import guide from '../services/guide.js'

const route = useRoute()
const authStore = useAuthStore()
const logoError = ref(false)
const logoLoaded = ref(false)
const avatarError = ref(false)
function isActive(path) {
  return route.path.startsWith(path)
}

const avatarSrc = computed(() => {
  if (authStore.isGuest) return null
  if (authStore.avatarThumb) return authStore.avatarThumb
  if (authStore.user?.avatar_url) return imgUrl(authStore.user.avatar_url)
  return null
})

// Значок горит, когда гость прошёл фазу 1, а цели не заполнены (фаза 2),
// либо есть непрочитанные уведомления от Зои: unread_zoya приходит в
// /auth/me (lib/zoyaNotices.js), гаснет при открытии чата (markZoyaSeen).
const phase2Pending = computed(() => {
  const u = authStore.user
  return !!(u?.is_guest && u?.onboarding_done && !u?.goals_data)
})
const unreadZoya = computed(() => Number(authStore.user?.unread_zoya) || 0)
const showZoyaBadge = computed(() => phase2Pending.value || unreadZoya.value > 0)
const badgeText = computed(() => {
  const n = unreadZoya.value + (phase2Pending.value ? 1 : 0)
  return n > 9 ? '9+' : String(Math.max(n, 1))
})

// Delayed badge appearance (0.5s)
const badgeVisible = ref(false)
let badgeTimer = null
watch(showZoyaBadge, (val) => {
  if (badgeTimer) { clearTimeout(badgeTimer); badgeTimer = null }
  if (val) {
    badgeTimer = setTimeout(() => { badgeVisible.value = true }, 1000)
  } else {
    badgeVisible.value = false
  }
}, { immediate: true })

// Fix iOS viewport glitch when returning from external page (e.g. T-Bank payment)
function forceViewportRecalc() {
  window.scrollTo(0, window.scrollY)
}
onMounted(() => {
  document.addEventListener('visibilitychange', forceViewportRecalc)
  window.addEventListener('pageshow', forceViewportRecalc)
})
onUnmounted(() => {
  document.removeEventListener('visibilitychange', forceViewportRecalc)
  window.removeEventListener('pageshow', forceViewportRecalc)
})
</script>

<style scoped>
.nav-outer {
  background: rgba(235, 225, 210, 0.6);
  -webkit-backdrop-filter: blur(20px);
  backdrop-filter: blur(20px);
  /* Полоска home indicator не должна лежать на иконках: фон тянется под
     неё, а ряд вкладок остаётся своей высоты выше. Инсет ненулевой только
     там, где полоска есть (iOS-приложение, PWA, жесты Android). */
  padding-bottom: var(--nav-inset);
}

.nav-content {
  height: 72px;
}

.nav-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  transition: background-color 0.15s;
  cursor: pointer;
}

/* Перевёрнутые скругления у активного таба (вверху) */
.nav-active::before,
.nav-active::after {
  content: '';
  position: absolute;
  top: 0;
  width: 14px;
  height: 14px;
  background-color: var(--cream);
}
.nav-active::before {
  left: -14px;
  -webkit-mask: radial-gradient(circle at bottom left, transparent 14px, black 14.5px);
  mask: radial-gradient(circle at bottom left, transparent 14px, black 14.5px);
}
.nav-active::after {
  right: -14px;
  -webkit-mask: radial-gradient(circle at bottom right, transparent 14px, black 14.5px);
  mask: radial-gradient(circle at bottom right, transparent 14px, black 14.5px);
}
/* Первый таб: нет левого скругления */
.nav-active.nav-first::before { display: none; }
/* Последний таб: нет правого скругления */
.nav-active.nav-last::after { display: none; }

/* Без теней */
.nav-tab, .nav-active {
  box-shadow: none;
}

/* Notification badge */
.zoya-badge {
  position: absolute;
  top: 2px;
  right: calc(50% - 32px);
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  background: #E53935;
  color: #fff;
  font-size: var(--fs-11);
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  line-height: 1;
  font-family: Inter, sans-serif;
}
.badge-fade-enter-active { transition: opacity 0.3s ease; }
.badge-fade-enter-from { opacity: 0; }
</style>
